<template>
  <div class="login-container">
    <div class="language-switcher-container">
      <LanguageSwitcher />
    </div>
    
    <div class="login-card card">
      <h1 class="login-title">{{ currentMode === 'login' ? $t('auth.login.title') : $t('auth.register.title') }}</h1>
      <p class="login-subtitle">{{ currentMode === 'login' ? $t('auth.login.subtitle') : $t('auth.register.subtitle') }}</p>
      
      <div v-if="error" class="error-message">
        {{ error }}
      </div>
      
      <!-- Login Form -->
      <form v-if="!showRegister" @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <input
            v-model="username"
            type="text"
            :placeholder="$t('auth.login.username')"
            class="input"
            required
          />
        </div>
        
        <div class="form-group">
          <input
            v-model="password"
            type="password"
            :placeholder="$t('auth.login.password')"
            class="input"
            required
          />
        </div>
        
        <button 
          type="submit" 
          class="btn btn-primary w-full"
          :disabled="loading"
        >
          <span v-if="loading" class="spinner"></span>
          <span v-else>{{ $t('auth.login.submit') }}</span>
        </button>
      </form>
      
      <!-- Register Form -->
      <form v-if="showRegister" @submit.prevent="handleRegister" class="login-form">
        <div class="form-group">
          <input
            v-model="registerData.username"
            type="text"
            :placeholder="$t('auth.register.username')"
            class="input"
            required
            minlength="3"
          />
        </div>
        
        <div class="form-group">
          <input
            v-model="registerData.password"
            type="password"
            :placeholder="$t('auth.register.password')"
            class="input"
            required
            minlength="6"
          />
        </div>
        
        <div class="form-group">
          <input
            v-model="registerData.confirmPassword"
            type="password"
            :placeholder="$t('auth.register.confirmPassword')"
            class="input"
            required
            minlength="6"
          />
        </div>

        <div class="form-group">
          <input
            v-model="registerData.invitationCode"
            type="text"
            :placeholder="$t('auth.register.invitationCode')"
            class="input"
            required
          />
        </div>
        
        <button 
          type="submit" 
          class="btn btn-secondary w-full"
          :disabled="loading || !isRegisterValid"
        >
          <span v-if="loading" class="spinner"></span>
          <span v-else>{{ $t('auth.register.submit') }}</span>
        </button>
      </form>
      
      <!-- Toggle between forms -->
      <div class="toggle-form">
        <p v-if="!showRegister">
          {{ $t('auth.login.noAccount') }}
          <a href="#" @click.prevent="showRegister = true" class="link">
            {{ $t('auth.login.registerLink') }}
          </a>
        </p>
        <p v-else>
          {{ $t('auth.register.hasAccount') }}
          <a href="#" @click.prevent="showRegister = false" class="link">
            {{ $t('auth.register.loginLink') }}
          </a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { authAPI } from '../services/api'
import LanguageSwitcher from '../components/LanguageSwitcher.vue'

export default {
  name: 'Login',
  components: {
    LanguageSwitcher
  },
  data() {
    return {
      username: '',
      password: '',
      error: '',
      loading: false,
      showRegister: false,
      registerData: {
        username: '',
        password: '',
        confirmPassword: '',
        invitationCode: ''
      }
    }
  },
  computed: {
    currentMode() {
      return this.showRegister ? 'register' : 'login'
    },
    isRegisterValid() {
      return this.registerData.username && 
             this.registerData.password && 
             this.registerData.confirmPassword &&
             this.registerData.invitationCode &&
             this.registerData.password === this.registerData.confirmPassword &&
             this.registerData.password.length >= 6 &&
             this.registerData.username.length >= 3
    }
  },
  methods: {
    async handleLogin() {
      this.loading = true
      this.error = ''
      
      try {
        const response = await authAPI.login({
          username: this.username,
          password: this.password
        })
        
        // Store token and user data
        localStorage.setItem('token', response.data.token)
        localStorage.setItem('user', JSON.stringify(response.data.user))
        
        // Redirect to dashboard
        this.$router.push('/dashboard')
      } catch (error) {
        this.error = error.response?.data?.message || this.$t('errors.generic')
      } finally {
        this.loading = false
      }
    },
    
    async handleRegister() {
      this.loading = true
      this.error = ''
      
      // Validate passwords match
      if (this.registerData.password !== this.registerData.confirmPassword) {
        this.error = this.$t('auth.validation.passwordsNoMatch')
        this.loading = false
        return
      }
      
      try {
        const response = await authAPI.register({
          username: this.registerData.username,
          password: this.registerData.password,
          invitationCode: this.registerData.invitationCode
        })
        
        // Store token and user data
        localStorage.setItem('token', response.data.token)
        localStorage.setItem('user', JSON.stringify(response.data.user))
        
        // Redirect to dashboard
        this.$router.push('/dashboard')
      } catch (error) {
        this.error = error.response?.data?.message || this.$t('errors.generic')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, #0f0c29 0%, #1a1a2e 40%, #16213e 100%);
}

.language-switcher-container {
  position: absolute;
  top: 2rem;
  right: 2rem;
  z-index: 10;
}

.login-card {
  width: 100%;
  max-width: 400px;
  position: relative;
}

.login-title {
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, #00c6ff, #0072ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.login-subtitle {
  text-align: center;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 2rem;
  font-size: 1rem;
}

.login-form {
  margin-bottom: 1.5rem;
}

.toggle-form {
  text-align: center;
  margin-top: 1rem;
}

.toggle-form p {
  color: rgba(255, 255, 255, 0.45);
}

.link {
  color: #00c6ff;
  text-decoration: none;
  font-weight: 600;
}

.link:hover {
  text-decoration: underline;
}
</style> 